import React, { useEffect, useRef, useState } from "react";
import { parseUntilObject } from "../../utilities/conversions";
import axios from "axios";
import { useParams } from "react-router-dom";
import InviteModal from "../../components/ui/agents/invitemodal";

/**
 * VcomJitsi.jsx
 *
 * Full single-file React component converted from the provided PHP/HTML page.
 * - No PHP; all server values are passed in via props
 * - Dynamically loads the Jitsi external_api script
 * - Uses a WebSocket for realtime messages
 * - Implements the same modals, timer, and behaviours as the original page
 *
 * Props (all optional — defaults provided):
 * - callDetail: { id, cid, duration }
 * - agent: { uid, name }
 * - metaValue: { alert_minutes_before, alert_beep_message }
 * - footerSettings: { fav_icon }
 * - serverName: hostname string (e.g. "example.com" or "localhost")
 * - apiBase: base URL string for REST endpoints (e.g. "https://example.com/")
 * - jitsiDomain: domain that serves external_api.js (default: 'vcom.jablabla.com')
 *
 * Important: Replace fetch endpoints (update-status, getCallDetail) with working endpoints
 * in your environment. This component does not depend on jQuery or Bootstrap.
 */
export default function JoinBroadcastCustomer({
    baseUrl,
    currentUser,
    // callDetail = { id: 123, cid: "meeting-123", duration: 15 },
    // agent = { uid: 42, name: "Agent" },
    // metaValue = { alert_minutes_before: 1, alert_beep_message: "/assets/ringtone.mp3" },
    // footerSettings = { fav_icon: "favicon.ico" },
    // serverName = typeof window !== 'undefined' ? window.location.hostname : 'localhost',
    // apiBase = '/',
    // jitsiDomain = 'vcom.jablabla.com',
}: { baseUrl: string, currentUser: any }) {
    // refs and state
    const jitsiDomain = 'vcom.jablabla.com';
    let { customer, broadcastId } = useParams<{
        customer: string;
        broadcastId: string;
    }>();
    const [callDetail, setCallDetail] = useState<any>(null);
    const [agent, setAgent] = useState<any>(null);
    // const [customer, setCustomer] = useState<any>(null);
    const [alertBeepMessage, setAlertBeepMessage] = useState("");
    const [alertMinutesBefore, setAlertMinutesBefore] = useState(1);
    const [loading, setLoading] = useState(true);


    const jitsiContainerRef = useRef<HTMLDivElement>(null);
    const apiObjRef = useRef(null);
    const wsRef = useRef<WebSocket | undefined>(null);
    const timerRef = useRef<number | undefined>(undefined);
    const [meetingDurationText, setMeetingDurationText] = useState(() => localStorage.getItem('meetingDuration') || null);
    const [isWaitingModalOpen, setWaitingModalOpen] = useState(false);
    const [isHoldModalOpen, setHoldModalOpen] = useState(false);
    const [isAddedMinutesModalOpen, setAddedMinutesModalOpen] = useState(false);
    const [addedMinutesText, setAddedMinutesText] = useState('');
    const [guestSpeakerName, setGuestSpeakerName] = useState('');
    const [open, setOpen] = useState(false);

    const [maxDuration, setMaxDuration] = useState(0);
    const [onceAlerted, setOnceAlerted] = useState(false);
    const musicRef = useRef(new Audio(alertBeepMessage || 'https://chamcore.com/chameleon/assets/ringtone.mp3'));

    /** Fetch call details by eventKey */
    const fetchBooking = async () => {
        try {
            const res = await axios.get(`${baseUrl}/getBroadcastById`, {
                params: { broadcastId: broadcastId, customer: customer, token: currentUser?.token },
            });
            setCallDetail(res.data.callDetail);
            setAgent(res.data.agent);
        } catch (err) {
            console.error("Error fetching call:", err);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {


        if (broadcastId) fetchBooking();
    }, [broadcastId, baseUrl]);

    // Build websocket url similar to the original: ws://host:8013/echo (localhost) or wss://wss.host/echo
    function getWsUrl() {
        if (baseUrl.includes("localhost")) {
            return `ws://localhost:8013/echo`;
        }
        return `wss://wss.chamcore.com/echo`;
    }

    // WebSocket message handler
    function handleWsMessage(raw: any) {
        if (!raw) return;
        let data;
        try {
            data = parseUntilObject(raw);
        } catch (err) {
            // ignore invalid messages
            return;
        }

        // only act on messages for this agent
        if (String(data?.information?.agent_id) !== String(agent.uid)) return;

        switch (data.about) {
            case 'end_booking_call_now':
                localStorage.removeItem('meetingDuration');
                break;
            default:
                break;
        }
    }

    // Timer logic (stores mm:ss in localStorage meetingDuration)
    function startTimer(initialSeconds = 0) {
        if (timerRef.current !== undefined) {
            clearTimeout(timerRef.current);
        }
        let cnt = parseInt(initialSeconds.toString(), 10) || 0;

        function tick() {
            cnt += 1;
            const hours = Math.floor(cnt / 3600);
            const minutes = Math.floor((cnt / 60) % 60);
            const seconds = cnt % 60;
            const hh = hours < 10 ? '0' + hours : String(hours);
            const mm = minutes < 10 ? '0' + minutes : String(minutes);
            const ss = seconds < 10 ? '0' + seconds : String(seconds);

            const timeeee = `${mm}:${ss}`;
            localStorage.setItem('meetingDuration', timeeee);
            setMeetingDurationText(timeeee);

            // if ((parseInt(maxDuration.toString(), 10) - parseInt(alertMinutesBefore.toString(), 10)) === minutes && !onceAlerted) {
            //     // show about-to-end modal & sound beep
            //     setOnceAlerted(true);
            //     musicRef.current.play().catch(() => { /* autoplay may be blocked */ });
            // }

            // continue
            timerRef.current = setTimeout(tick, 1000);
        }
        timerRef.current = setTimeout(tick, 1000);
    }

    // Load Jitsi external_api.js dynamically
    function loadJitsiScript() {
        return new Promise((resolve, reject) => {
            if (window.JitsiMeetExternalAPI) return resolve(window.JitsiMeetExternalAPI);
            const src = `https://${jitsiDomain}/external_api.js`;
            const s = document.createElement('script');
            s.src = src;
            s.async = true;
            s.onload = () => resolve(window.JitsiMeetExternalAPI);
            s.onerror = reject;
            document.head.appendChild(s);
        });
    }

    // Start Jitsi meeting with options
    async function startMeeting(meetingId: string, dispName: string) {
        if (!meetingId) return;
        if (!callDetail) return;
        if (callDetail?.status === "completed" || (schedYmd && schedYmd < todayYmd)) return;
        if (callDetail.start_date_time > todayYmdHis) return;
        try {
            const Jitsi = await loadJitsiScript();
            let toolbarButtons = JSON.parse(callDetail.participants_permissions);

            let toolbar = [];
            for (let key in toolbarButtons) {
                if (!Object.hasOwn(toolbarButtons, key)) continue;

                const element = toolbarButtons[key];

                key = key.replaceAll("vcom_", "");
                key = key.replaceAll("_", "-");
                if (key === "screen-sharing") { key = "desktop"; }

                toolbar.push(key);

            }
            let liveStreaming = false || {};
            if (toolbar.includes("liveStreaming")) {
                liveStreaming = {
                    // Whether to enable live streaming or not.
                    enabled: true,
                    // Terms link
                    termsLink: 'https://www.youtube.com/t/terms',
                    // Data privacy link
                    dataPrivacyLink: 'https://policies.google.com/privacy',
                    // RegExp string that validates the stream key input field
                    validatorRegExpString: '^(?:[a-zA-Z0-9]{4}(?:-(?!$)|$)){4}',
                    // Documentation reference for the live streaming feature.
                    helpLink: 'https://jitsi.org/live'
                };
            }
            const options = {
                roomName: meetingId,
                width: '100%',
                height: '100%',
                parentNode: jitsiContainerRef.current,
                userInfo: { displayName: dispName },
                configOverwrite: {
                    notifications: [], // we'll disable selectively below
                    disableDeepLinking: true,
                    prejoinPageEnabled: false,
                    enableWelcomePage: false,
                    disableTileView: false,
                    toolbarButtons: toolbar,
                    liveStreaming: liveStreaming,
                    buttonsWithNotifyClick: [
                        'end-meeting',
                        'hangup',
                        'hangup-menu',
                        'invite'
                    ]
                },
                interfaceConfigOverwrite: {
                    TOOLBAR_ALWAYS_VISIBLE: true,
                },
            };

            // create the Jitsi object
            const api = new Jitsi(jitsiDomain, options);
            apiObjRef.current = api;

            // plain equivalent of the event listeners used in the original
            api.addListener('videoConferenceJoined', () => {
                // nothing to do straight away in our conversion — placeholder
                api.executeCommand('toggleTileView');
            });

            api.addEventListeners({
                readyToClose: () => {
                    // placeholder: original code updated agent status on meeting end
                },
                participantJoined: (data: any) => {
                    setWaitingModalOpen(false);

                    // restore timer if localStorage value exists
                    const ld = localStorage.getItem('meetingDuration');
                    if (ld) {
                        const parts = ld.split(':');
                        if (parts.length === 2) {
                            const minutes = parseInt(parts[0], 10);
                            const seconds = parseInt(parts[1], 10);
                            const totalSeconds = (minutes * 60) + seconds;
                            startTimer(totalSeconds);
                            return;
                        }
                    }
                    startTimer(0);
                },
                toolbarButtonClicked: (object: any) => {
                    const key = object?.key;
                    if (key === 'end-meeting' || key === 'hangup' || key === 'hangup-menu') {
                        localStorage.removeItem('meetingDuration');
                        window.location.href = window.location.origin;

                    } else if (key === "invite") {
                        setOpen(true);
                    }
                }
            });

        } catch (err) {
            console.error('Failed to load Jitsi API', err);
        }
    }

    useEffect(() => {
        // set document title and favicon (mirrors PHP <title> and favicon usage)
        // document.title = `MysticConnect VCOM`;

        // open waiting modal on mount
        // setWaitingModalOpen(true);

        // WebSocket connect
        const wsUrl = getWsUrl();
        try {
            const conn = new WebSocket(wsUrl);
            wsRef.current = conn;
            conn.onmessage = (e) => handleWsMessage(e.data);
        } catch (e) {
            console.warn('WebSocket connection failed', e);
        }

        // start jitsi
        const dispName = customer || 'Participant';
        startMeeting(callDetail?.broadcast_id, dispName);

        // When the component unmounts cleanup
        return () => {
            try { apiObjRef.current?.dispose(); } catch (e) { }
            try { wsRef.current?.close(); } catch (e) { }
            clearTimeout(timerRef.current);
        };
    }, [callDetail, agent]); // run once

    // Small helper: human-friendly display of agent name
    const [showAgentName, setShowAgentName] = useState('Agent');

    useEffect(() => {
        setShowAgentName(agent?.name);
    }, [agent])

    const handleSend = async ({ recipients, names, message }) => {
        console.log("Sending invite:", recipients, names, message);
        try {
            let formData = new FormData();

            formData.append("token", currentUser.token);
            formData.append("names", names);
            formData.append("recipients", recipients);
            formData.append("message", message);
            formData.append("broadcast_id", callDetail.broadcast_id);

            const response = await axios.post(baseUrl + '/addGuestInBroadcast', formData);
            return response.data;

        } catch (err) {
            console.error(err);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen text-white">
                Loading broadcast...
            </div>
        );
    }

    if (!callDetail) {
        return (
            <div className="flex items-center justify-center h-screen text-red-500">
                Broadcast not found
            </div>
        );
    }
    // compare schedule date (Y-m-d H:i:s) with today's date (Y-m-d)
    const getYmd = (d: Date) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const getYmdHis = (d: Date) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
    }

    const schedYmd = callDetail?.start_date_time
        ? (callDetail.start_date_time as string).split(' ')[0]
        : null;
    const todayYmd = getYmd(new Date());
    const todayYmdHis = getYmdHis(new Date());

    if (callDetail?.status === "completed" || (schedYmd && schedYmd < todayYmd)) {
        return (
            <div className="flex items-center justify-center h-screen text-red-500">
                Broadcast ended.
            </div>
        );
    }
    if (callDetail.start_date_time > todayYmdHis) {
        return (
            <div className="flex items-center justify-center h-screen text-red-500">
                <h1 className="text-2xl">Broadcast will start at: {callDetail.start_date_time}</h1>
            </div>
        );
    }

    // UI: simple Tailwind-based modals and Jitsi container
    return (
        <div className="min-h-screen bg-black text-white">
            <div ref={jitsiContainerRef} style={{ height: '97.7vh' }} id="jitsi-meet-conf-container" />

            {/* Waiting Participant Modal */}
            {isWaitingModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/60" onClick={() => { }} />
                    <div className="relative bg-orange-400 rounded-lg p-6 w-80">
                        <div className="flex flex-col items-center">
                            <img src="https://placeimg.com/400/400/people" alt="avatar" className="rounded-full w-32 h-32" />
                            <p id="in_video_call" className="text-black text-center mt-4">Please hold<br />we are connecting</p>
                            <p className="text-black text-center mt-2 font-semibold" id="show_name">{showAgentName}</p>
                            <div className="w-full text-center my-4">
                                <div className="inline-block animate-spin border-4 border-transparent border-t-black rounded-full w-10 h-10" />
                            </div>
                            <div className="text-center">
                                <button className="bg-red-600 text-white px-4 py-2 rounded-full" onClick={() => setWaitingModalOpen(false)}>Hide</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Hold The Line Modal */}
            {isHoldModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/60" />
                    <div className="relative bg-orange-400 rounded-lg p-6 w-80">
                        <div className="flex flex-col items-center">
                            <img src="https://placeimg.com/400/400/people" alt="avatar" className="rounded-full w-32 h-32" />
                            <input type="hidden" id="video_call_id" value={callDetail.broadcast_id} />
                            <div id="buy_more_minutes_text" className="text-black text-center mt-4">Please <br /> hold the line.</div>
                            <div className="w-full text-center my-4">
                                <div className="inline-block animate-spin border-4 border-transparent border-t-black rounded-full w-10 h-10" />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Added More Minutes Modal */}
            {isAddedMinutesModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setAddedMinutesModalOpen(false)} />
                    <div className="relative bg-gray-800 rounded-lg p-6 w-80">
                        <div className="flex flex-col items-center">
                            <h3 className="text-white">..</h3>
                            <p id="added-more-minutes-text" className="text-center text-white mt-4">{addedMinutesText}</p>
                            <div className="mt-4">
                                <button className="bg-blue-600 px-4 py-2 rounded" onClick={() => setAddedMinutesModalOpen(false)}>OK</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Invite Modal */}
            <InviteModal
                isOpen={open}
                onClose={() => setOpen(false)}
                onSend={handleSend}
                copyLink={window.location.origin + "/guest/broadcast/" + guestSpeakerName + "/" + broadcastId}
            />
            {/* Small hidden styles replicated from original */}
            <style>{`
        span[data-role="remove"]::after { content: "x" !important; top: -8px !important; padding-left: 10px !important; }
        .notifications-container { display: none !important; }
      `}</style>
        </div>
    );
}
